import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT, useLocale } from "@/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { LogOut, Shield, CreditCard, Sparkles } from "lucide-react";

function formatShortDate(iso, locale) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(locale === "en" ? "en-GB" : "it-IT", {
      day: "2-digit",
      month: "short",
    });
  } catch {
    return "";
  }
}

function SubscriptionBadge({ user }) {
  const t = useT();
  const { locale } = useLocale();
  if (user?.is_super_admin) return null;
  const status = user?.subscription_status;
  const cancel = user?.cancel_at_period_end;
  const trialEnd = formatShortDate(user?.trial_end, locale);
  const periodEnd = formatShortDate(user?.current_period_end, locale);

  let label = null;
  let tone = "text-white/50 border-white/[0.1]";
  if (status === "trialing") {
    label = t("shell.statusTrial", { date: trialEnd || "—" });
    tone = "text-emerald-300 border-emerald-400/30 bg-emerald-400/10";
  } else if (status === "active" && cancel) {
    label = t("shell.statusCancelAt", { date: periodEnd || "—" });
    tone = "text-amber-300 border-amber-400/30 bg-amber-400/10";
  } else if (status === "active") {
    label = t("shell.statusActive");
    tone = "text-emerald-300 border-emerald-400/30 bg-emerald-400/10";
  } else if (status === "past_due") {
    label = t("shell.statusPastDue");
    tone = "text-red-300 border-red-400/30 bg-red-500/10";
  } else if (user?.has_paid) {
    label = t("shell.statusGenericActive");
    tone = "text-emerald-300 border-emerald-400/30 bg-emerald-400/10";
  } else if (!status && user?.has_access && trialEnd) {
    // Free trial backend-driven (Feb 2026): no Stripe status yet.
    label = t("shell.statusFreeTrial", { date: trialEnd });
    tone = "text-emerald-300 border-emerald-400/30 bg-emerald-400/10";
  }
  if (!label) return null;
  return (
    <Link
      to="/subscribe"
      data-testid="subscription-badge"
      className={`hidden md:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm font-geist-mono text-[10px] uppercase tracking-wider border ${tone} hover:opacity-80 transition-opacity`}
    >
      <Sparkles className="h-3 w-3" />
      {label}
    </Link>
  );
}

export default function AppShell({ subtitle, children }) {
  const { user, logout } = useAuth();
  const t = useT();
  const loc = useLocation();
  const isSuper = !!user?.is_super_admin;
  const TABS = [
    { to: "/", label: t("nav.tabs.batch"), key: "batch" },
    { to: "/csv", label: t("nav.tabs.csv"), key: "csv" },
    { to: "/form", label: t("nav.tabs.form"), key: "form" },
  ];
  return (
    <div className="min-h-screen flex flex-col bg-[#050B1F] text-white font-geist">
      <header className="border-b border-white/[0.08] bg-[#050B1F]/95 backdrop-blur-sm relative">
        <div className="absolute inset-0 auth-grid pointer-events-none opacity-40" />
        <div className="relative flex items-center justify-between px-6 h-16">
          <div className="flex items-center gap-4">
            <div className="h-8 w-8 rounded-sm bg-gradient-to-br from-[#4D8AFF] to-[#002FA7] flex items-center justify-center shadow-[0_0_20px_-4px_rgba(77,138,255,0.55)]">
              <span className="font-geist-mono text-[13px] font-bold text-white">R</span>
            </div>
            <div className="flex flex-col leading-tight">
              <div className="font-geist text-[15px] font-semibold text-white tracking-tight">
                Revo <span className="text-[#4D8AFF]">PDF</span> Loader
              </div>
              <div className="font-geist-mono text-[9px] tracking-[0.2em] uppercase text-white/40">
                by FudoSoftware
              </div>
            </div>
            <div className="font-geist-mono text-[11px] text-white/40 hidden md:block truncate max-w-[420px] ml-4 pl-4 border-l border-white/[0.08]">
              {subtitle}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <SubscriptionBadge user={user} />
            {!isSuper && (
              <Link
                to="/subscribe"
                data-testid="billing-link"
                className="hidden sm:inline-flex items-center gap-1.5 px-2 py-1 rounded-sm hover:bg-white/[0.06] text-white/70 hover:text-white font-geist-mono text-[11px] uppercase tracking-wider transition-colors"
              >
                <CreditCard className="h-3.5 w-3.5" /> {t("nav.subscribe")}
              </Link>
            )}
            <span className="font-geist-mono text-[11px] text-white/50 hidden sm:inline" data-testid="user-email">
              {user?.email}
            </span>
            <Button
              variant="ghost"
              size="sm"
              data-testid="logout-btn"
              onClick={logout}
              className="rounded-sm hover:bg-white/[0.06] hover:text-white text-white/70 font-geist-mono text-[11px] uppercase tracking-wider"
            >
              <LogOut className="h-3.5 w-3.5 mr-1" /> {t("nav.logout")}
            </Button>
          </div>
        </div>
        <nav className="relative flex border-t border-white/[0.08]" data-testid="mode-tabs">
          {TABS.map((tab) => {
            const active = loc.pathname === tab.to;
            return (
              <Link
                key={tab.key}
                to={tab.to}
                data-testid={`tab-${tab.key}`}
                className={`px-5 py-2.5 font-geist-mono text-[11px] tracking-wide uppercase border-r border-white/[0.08] transition-colors ${
                  active
                    ? "bg-[#4D8AFF] text-[#050B1F] font-semibold"
                    : "text-white/50 hover:text-white hover:bg-white/[0.04]"
                }`}
              >
                {tab.label}
              </Link>
            );
          })}
          {isSuper && (
            <Link
              to="/admin"
              data-testid="tab-admin"
              className={`px-5 py-2.5 font-geist-mono text-[11px] tracking-wide uppercase border-r border-white/[0.08] transition-colors flex items-center gap-1.5 ml-auto ${
                loc.pathname === "/admin"
                  ? "bg-[#4D8AFF] text-[#050B1F] font-semibold"
                  : "text-[#4D8AFF] hover:text-white hover:bg-white/[0.04]"
              }`}
            >
              <Shield className="h-3 w-3" /> {t("nav.tabs.admin")}
            </Link>
          )}
        </nav>
      </header>
      <div className="flex-1">{children}</div>
      <footer className="relative border-t border-white/[0.08] bg-[#050B1F]/95 px-6 py-3 flex items-center justify-between font-geist-mono text-[10px] uppercase tracking-wider text-white/40">
        <div className="flex items-center gap-4">
          <span>Revo PDF Loader</span>
          <span className="hidden sm:inline">·</span>
          <span className="hidden sm:inline">© 2026 FudoSoftware</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden md:inline">build 2.1.0</span>
          <span className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            online
          </span>
        </div>
      </footer>
    </div>
  );
}
